import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { ProviderName, WorksheetItem } from '@fgb/core';
import { FgbFacebookPlayerComponent } from '../../providers/facebook/fgb-facebook-player/fgb-facebook-player.component';
import { FgbYoutubePlayerComponent } from '../../providers/youtube/fgb-youtube-player/fgb-youtube-player.component';
import { FgbSVPPlayerComponent } from '../../providers/streamingVideoProvider/fgb-svp-player/fgb-svp-player.component';
import { FgbTwitchPlayerComponent } from '../../providers/twitch/fgb-twitch-player/fgb-twitch-player.component';
import { FgbVimeoPlayerComponent } from '../../providers/vimeo/fgb-vimeo-player/fgb-vimeo-player.component';
import { FgbBoxcastPlayerComponent } from '../../providers/boxcast/fgb-boxcast-player/fgb-boxcast-player.component';
import { FgbStreamablePlayerComponent } from '../../providers/streamable/fgb-streamable-player/fgb-streamable-player.component';
import { take } from 'rxjs';
import { FgbAmgPlayerComponent } from '../../providers/amg/fgb-amg-player.component';
import { animate, style, transition, trigger } from '@angular/animations';
import { Playable } from 'src/app/shared/directives/playable.directive';

@Component({
  selector: 'fgb-media-player',
  templateUrl: './media-player.component.html',
  styleUrl: './media-player.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeOut', [transition(':leave', [style({ opacity: 1 }), animate('300ms ease-out', style({ opacity: 0 }))])]),
  ],
})
export class MediaPlayerComponent implements OnInit, OnChanges {
  @Input() video: WorksheetItem & { WorksheetId: number };
  player: any | null = null;
  @ViewChild('playerContainer', { read: ViewContainerRef, static: true })
  vcr!: ViewContainerRef;
  private cdr = inject(ChangeDetectorRef);
  isPlaying: boolean = false;

  ngOnInit(): void {
    this.loadComponent(this.video.ProviderId, false);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.video && !changes.video.firstChange) {
      this.isPlaying = false;
      this.loadComponent(this.video.ProviderId);
      this.cdr.markForCheck();
    }
  }

  loadComponent(provider: ProviderName, autoPlay: boolean = true): void {
    const componentType = this.getComponent(provider);
    if (!componentType) return;

    this.vcr.clear();

    const componentRef = this.vcr.createComponent(componentType);
    this.player = componentRef.instance;
    this.player.streamKey = this.video.ProviderKey;

    if (autoPlay && !(this.player instanceof Playable)) {
      // If the player is not playable, we'll just assume it's playing
      this.isPlaying = true;
      this.cdr.markForCheck();
      return;
    }

    if (autoPlay && this.player && this.player instanceof Playable) {
      this.player.onReady.pipe(take(1)).subscribe(() => {
        this.play();
        this.cdr.detectChanges();
      });
    }
  }

  play(): void {
    if (this.player && this.player instanceof Playable) {
      this.player.play();
      this.isPlaying = true;
    }
  }

  private getComponent(provider: ProviderName): Type<any> | null {
    switch (provider) {
      case ProviderName.StreamAMG:
        return FgbAmgPlayerComponent;
      case ProviderName.Facebook:
        return FgbFacebookPlayerComponent;
      case ProviderName.YouTube:
        return FgbYoutubePlayerComponent;
      case ProviderName.StreamingVideoProvider:
        return FgbSVPPlayerComponent;
      case ProviderName.Twitch:
        return FgbTwitchPlayerComponent;
      case ProviderName.Viemo:
        return FgbVimeoPlayerComponent;
      case ProviderName.Boxcast:
        return FgbBoxcastPlayerComponent;
      case ProviderName.Streamable:
        return FgbStreamablePlayerComponent;
      default:
        return null;
    }
  }
}
